import Papa from "papaparse";

const normaliseHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const normaliseAdviserName = (value) =>
  String(value || "")
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const nameVariants = (value) => {
  const text = String(value || "").trim();
  const variants = new Set([normaliseAdviserName(text)]);
  const withoutParentheses = text.replace(/\([^)]*\)/g, " ");
  variants.add(normaliseAdviserName(withoutParentheses));

  const nickname = text.match(/\(([^)]+)\)/)?.[1];
  const surname = text.replace(/^.*\)\s*/, "").trim();
  if (nickname && surname) variants.add(normaliseAdviserName(`${nickname} ${surname}`));

  return [...variants].filter(Boolean);
};

export const parseCurrencyAmount = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const text = String(value).trim();
  const negative = /^\(.*\)$/.test(text);
  const number = Number(text.replace(/[£,$()\s]/g, ""));
  if (!Number.isFinite(number)) return Number.NaN;
  return negative ? -Math.abs(number) : number;
};

const isoDate = (date) => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// England and Wales bank holidays that can affect this September 2026 migration.
const nonWorkingDates = new Set(["2026-08-31"]);

export const getLastWorkingDay = (monthKey) => {
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) {
    throw new Error("A valid month is required.");
  }
  const date = new Date(Date.UTC(year, month, 0));
  while (
    date.getUTCDay() === 0 ||
    date.getUTCDay() === 6 ||
    nonWorkingDates.has(isoDate(date))
  ) {
    date.setUTCDate(date.getUTCDate() - 1);
  }
  return isoDate(date);
};

export const getOpeningBalanceDate = (monthKey) => {
  const [year, month] = String(monthKey).split("-").map(Number);
  if (!year || !month || month < 1 || month > 12) {
    throw new Error("A valid month is required.");
  }
  const previousMonth = new Date(Date.UTC(year, month - 1, 1));
  previousMonth.setUTCMonth(previousMonth.getUTCMonth() - 1);
  return getLastWorkingDay(
    `${previousMonth.getUTCFullYear()}-${String(previousMonth.getUTCMonth() + 1).padStart(2, "0")}`,
  );
};

const adviserAliases = (adviser) => [
  adviser.name,
  [adviser.firstName, adviser.lastName].filter(Boolean).join(" "),
  [adviser.preferredName, adviser.lastName].filter(Boolean).join(" "),
  adviser.tradingName,
  adviser.tradingStyle,
  adviser.brandName,
].flatMap(nameVariants);

const buildUniqueAdviserIndex = (advisers) => {
  const candidates = new Map();
  advisers.forEach((adviser) => {
    const adviserKey = String(
      adviser.email || adviser.id || adviser.adviserId || "",
    ).trim().toLowerCase();
    if (!adviserKey) return;
    adviserAliases(adviser).forEach((alias) => {
      if (!candidates.has(alias)) candidates.set(alias, new Map());
      candidates.get(alias).set(adviserKey, adviser);
    });
  });
  return new Map(
    [...candidates.entries()]
      .filter(([, matches]) => matches.size === 1)
      .map(([alias, matches]) => [alias, [...matches.values()][0]]),
  );
};

const findHeader = (fields, expected) =>
  fields.find((field) => normaliseHeader(field) === expected);

export const parseOpeningBalanceCsv = (csvText, advisers = [], monthKey = "2026-09") => {
  const parsed = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (header) => String(header || "").trim(),
  });
  if (parsed.errors?.length) {
    throw new Error(parsed.errors[0].message || "The CSV could not be read.");
  }

  const fields = parsed.meta?.fields || [];
  const nameHeader = findHeader(fields, "name");
  const openingHeader = findHeader(fields, "opening balance");
  const advanceHeader = findHeader(fields, "advance");
  if (!nameHeader || (!openingHeader && !advanceHeader)) {
    throw new Error("The CSV must contain Name and either Opening Balance or Advance columns.");
  }

  const adviserIndex = buildUniqueAdviserIndex(advisers);
  const openingBalanceDate = getOpeningBalanceDate(monthKey);
  const advanceDate = getLastWorkingDay(monthKey);

  return parsed.data.reduce((rows, source, index) => {
    const sourceName = String(source[nameHeader] || "").trim();
    const openingBalance = openingHeader
      ? parseCurrencyAmount(source[openingHeader])
      : null;
    const advance = advanceHeader ? parseCurrencyAmount(source[advanceHeader]) : null;
    if (!sourceName || (openingBalance === null && advance === null)) return rows;
    if (Number.isNaN(openingBalance) || Number.isNaN(advance)) {
      throw new Error(`Row ${index + 2} contains an invalid amount.`);
    }
    if (advance !== null && advance < 0) {
      throw new Error(`Row ${index + 2} contains a negative advance.`);
    }

    const matchedAdviser = nameVariants(sourceName)
      .map((variant) => adviserIndex.get(variant))
      .find(Boolean);
    rows.push({
      rowNumber: index + 2,
      sourceName,
      openingBalance,
      advance,
      openingBalanceDate,
      advanceDate,
      adviserEmail: matchedAdviser?.email || matchedAdviser?.id || "",
      adviserName: matchedAdviser?.name || "",
      adviserId: matchedAdviser?.adviserId || "",
      brandName: matchedAdviser?.brandName || "",
      mappingStatus: matchedAdviser ? "Matched" : "Unmatched",
    });
    return rows;
  }, []);
};
